import { useContext, useState } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import { faCheck, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import List from '../../common/Widgets/List';
import ViewDocument from '../../common/ViewDocument/ViewDocument';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { PageAbsolutePath } from '../../../consts/Common';
import { setCountryCode } from '../../common/HelperMethods';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { InsuranceJourneyContext } from '../../common/CommonTypes';
import PopupComponent from '../../common/PopupComponent';
import BusinessTermsAncillaryPage from './BusinessTermsAncillaryPage';

const listIcon = <FontAwesomeIcon icon={faCheck} listItem />;
const pdfIcon = <FontAwesomeIcon icon={faFilePdf} />;

const confirmationItems = [
  `I can afford the monthly repayments and do not know of any pending or future events that would affect this.`,
  `I do not have any CCJ's (County Court Judgements).`,
  `I do not have any bankruptcies.`,
  `If I have previously had credit through Premium Credit Limited, I have not defaulted on the repayments.`
];

const SettingUpDirectDebitPage = (): JSX.Element => {
  const { site } = useContext(SiteContext);
  const navigate = useNavigate();
  const { pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { commonEvents } = pageEvents;
  const { onPageChange } = commonEvents;
  const [showPopupComponent, setShowPopupComponent] = useState(false);

  const onNextClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.PaymentPage,
      site.countryCode
    );
    navigate(path);
    onPageChange(PageAbsolutePath.Insurance.PaymentPage, true);
  };

  const renderBusinessTermsAncillaryPage = () => {
    return <BusinessTermsAncillaryPage isShowingInPopup />;
  };

  const onClosePopup = () => {
    setShowPopupComponent(false);
  };

  return (
    <div className='no-stepper'>
      <h5 className='section-header m3-top'>
        Paying monthly: Setting Up Your Direct Debit
      </h5>
      <h6 className='section-header m3-top'>
        <b>Information regarding the finance agreement</b>
      </h6>
      <p>
        By proceeding with the monthly payment option, you are applying to enter
        into a finance agreement. This means that once you have completed the
        purchase of your insurance, your details will be passed to Premium
        Credit Limited (PCL). PCL will then carry out the appropriate credit
        checks to ensure this finance is suitable and this will leave a
        footprint on your credit file which is visible to other lenders who may
        use this information to assess future applications. Should it be
        established that finance cannot be offered as a result of the credit
        checks, PCL shall write to you to confirm this with the relevant
        information. You will then be required to pay for your insurance as a
        one-off lump sum payment if you wish to continue – please refer to our{' '}
        <Link
          id='lnkTermsOfBusinessInText'
          className='in-text inherited'
          onClick={() => setShowPopupComponent(true)}
          to=''>
          Terms of Business
        </Link>{' '}
        for further details on this process.
      </p>
      <p>
        Your Welcome Pack will be sent to you via post or email, depending on
        the preference you have selected, within 14 working days and will
        contain further information about the company and your agreement.
      </p>
      <p>
        Please be aware that failing to meet your instalment repayments will
        result in a default charge being applied by PCL. Defaults may be
        registered on your credit file which can impact your future ability to
        obtain credit. There is also a charge applied by PCL for late repayments
        and these may also be registered on your credit file which could impact
        your future ability to obtain credit. Full details of the charges
        applied will be detailed in your Credit Agreement sent to you by PCL.
        You can also contact us or PCL at any time for details of the credit
        reference agencies used.
      </p>
      <p>
        If you have any questions regarding the finance offered, please contact
        us on {site.brand.brandPhoneNumbers.quotesAndRenewalsNumber}.
      </p>

      <h6 className='section-header m3-top'>
        <b>Declaration</b>
      </h6>
      <span>I confirm that by proceeding with this policy:</span>
      <div className={`confirmation-item-list-${site.brand.brandName}`}>
        <List
          id='confirmation-items'
          listItems={confirmationItems}
          icon={listIcon}
        />
      </div>

      <p>
        If any of the above statements are not true, your application for
        finance may be declined and you will be asked to pay for your insurance
        as a one-off lump sum instead. Please contact us on{' '}
        {site.brand.brandPhoneNumbers.quotesAndRenewalsNumber} if you have any
        concerns.
      </p>

      <h6 className='section-header m3-top'>
        <b>Account Details</b>
      </h6>
      <p>
        We now need your account details to allow us to take your monthly
        payments. Please be reassured this is a secure SSL encrypted service, so
        your details are safe. All Direct Debits are protected by a guarantee,
        details will be provided with your documents, however, if you would like
        to review this information now, please see below for our downloadable
        document.
      </p>
      <p>
        These documents will open in a new pop-up window. You must view the
        documents before proceeding. If they are not opening, you may have
        pop-ups blocked on your device and will need to adjust your settings to
        allow this. If you need any help, please contact us.
      </p>

      <ViewDocument
        path={`docs/Direct debit Gurantee.pdf`}
        documentName='Direct Debit Guarantee'
        icon={pdfIcon}
      />

      <div className='flex-row form-nav'>
        <ButtonBack
          name='btn-back'
          text='Back'
          onClick={() =>
            navigate(PageAbsolutePath.Insurance.ImportantNoticeInformation)
          }
          additionalClassNames='btn-back'
        />

        <ButtonContinue
          name='btn-Next'
          text='Next'
          onClick={onNextClick}
          additionalClassNames='btn-continue'
        />
      </div>
      <PopupComponent
        showPopupComponent={showPopupComponent}
        renderComponent={renderBusinessTermsAncillaryPage}
        onClosePopup={onClosePopup}
        popupTitle='Terms of Business'
      />
    </div>
  );
};

export default SettingUpDirectDebitPage;
